import React, { useEffect, useRef, useState } from "react"
import { Spinner } from "reactstrap"

const CHEVRON_GAP = 10 // guaranteed minimum space between the label and the chevron

/** The taller, darker look Keyword/Category Tracking had before this component
 *  existed. Everything else gets the (now-default) Weekly Insights theme, so
 *  spread this in only where that older look still needs to match surrounding
 *  UI: `<FlagSelectDropdown {...KEYWORD_TRACKING_THEME} ... />`. */
export const KEYWORD_TRACKING_THEME = {
  height: 40,
  background: "#2d2e3f",
  borderColor: "#3A3B47",
  selectedOptionColor: "#fff",
  selectedOptionBackground: "#3A3B47",
  dividerBeforeChevron: false,
}

/**
 * The single dropdown look for a "pick one from a short list" control — built to
 * replace the country and language pickers that KeywordTracking and
 * CategoryTracking each hand-rolled independently. Country was a custom
 * flag+chevron `<div>`; language next to it was a bare native `<select>`, whose
 * browser-drawn arrow never quite matched the custom one beside it — that
 * mismatch is what looked "misaligned". Since both now render through this one
 * component, there's no second implementation left to drift out of sync with.
 *
 * Deliberately a custom popup rather than a native `<select>`: a native
 * control's arrow/box chrome is drawn by the OS and differs across
 * Chrome/Firefox/Safari, which is exactly the inconsistency this replaces.
 *
 * Works for both a fixed list and a per-caller dynamic one — `options` is just
 * an array, so a caller derives it however it needs to (e.g. countries fetched
 * from `/countries`, languages derived per-selected-country from
 * `country.locales`). Entries can be plain strings or `{ value, label }`
 * objects when the label shown to the user should differ from the value sent
 * back through `onChange`.
 */
export default function FlagSelectDropdown({
  options = [],
  value,
  onChange,
  placeholder = "Select",
  disabled = false,
  /** The options themselves aren't ready yet (e.g. the country list is still
   *  being fetched) — renders a spinner in a box the same size as the real
   *  control, so swapping the two doesn't shift anything else on the page. */
  loading = false,
  /** `(optionValue) => ReactNode | null` — e.g. a country flag. Omit for a
   *  plain text list (language has no icon). */
  icon,
  minWidth = 140,
  maxWidth = 220,
  noOptionsText = "No options",
  className = "",
  /** A thin vertical rule between the label and the chevron — the default
   *  look (matches Top Discount). Trending uses the plain-chevron look
   *  instead, so it passes `dividerBeforeChevron={false}` explicitly rather
   *  than this being something every other caller has to opt into. */
  dividerBeforeChevron = true,
  /** These five default to Weekly Insights' theme (`#171821`, 32px,
   *  `rgba(53,55,72,1)` border, pink text for the selected row, no persistent
   *  background on it) since that's the intended look for most new call
   *  sites. Keyword/Category Tracking's older, taller, darker look is the
   *  exception — they pass `KEYWORD_TRACKING_THEME` (exported above) rather
   *  than being the default everyone else has to override. */
  height = 32,
  background = "#171821",
  borderColor = "rgba(53, 55, 72, 1)",
  selectedOptionColor = "#F6255A",
  selectedOptionBackground = "transparent",
}) {
  const [open, setOpen] = useState(false)
  const [hovered, setHovered] = useState(false)
  const containerRef = useRef(null)

  const normalized = options.map((opt) =>
    typeof opt === "string" ? { value: opt, label: opt } : opt
  )
  const selected = normalized.find((opt) => opt.value === value)
  const isDisabled = disabled || loading

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [open])

  // Collapse if this control becomes disabled while its menu is open (e.g. a
  // tracking request kicks off right after the user opened it).
  useEffect(() => {
    if (isDisabled) setOpen(false)
  }, [isDisabled])

  if (loading) {
    return (
      <div
        className={`flag-select-dropdown${className ? ` ${className}` : ""}`}
        style={{ position: "relative", minWidth, maxWidth, flexShrink: 0 }}
      >
        <div
          style={{
            backgroundColor: background,
            border: `1px solid ${borderColor}`,
            borderRadius: "6px",
            width: "100%",
            height: `${height}px`,
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spinner size="sm" color="light" />
        </div>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={`flag-select-dropdown${className ? ` ${className}` : ""}`}
      style={{ position: "relative", minWidth, maxWidth, flexShrink: 0 }}
    >
      <div
        onClick={() => !disabled && setOpen((prev) => !prev)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault()
            setOpen((prev) => !prev)
          }
        }}
        style={{
          backgroundColor: disabled ? "#4b5563" : background,
          color: "#fff",
          border: `1px solid ${!disabled && (open || hovered) ? "#5a5c6b" : borderColor}`,
          borderRadius: "6px",
          padding: "8px 12px",
          fontSize: "14px",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          width: "100%",
          height: `${height}px`,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          transition: "border-color 0.2s ease",
        }}
      >
        {/* `flex: 1` is what actually pushes the chevron to the box's right edge —
            `justify-content: space-between` alone only creates a gap when the box
            has leftover width beyond both children's natural size, which a short
            label (e.g. "english") in a narrow box often doesn't leave, so the
            chevron ends up sitting flush against the text instead. */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          {selected && icon ? icon(selected.value) : null}
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              // The underlying labels (language names in particular, e.g. "english")
              // arrive lowercase from the API — this is display-only, so `value`
              // sent through `onChange` is untouched and comparisons elsewhere
              // against the raw string keep working.
              textTransform: "capitalize",
            }}
          >
            {selected ? selected.label : placeholder}
          </span>
        </div>
        {dividerBeforeChevron ? (
          <span
            aria-hidden="true"
            style={{
              width: "1px",
              height: "18px",
              backgroundColor: "#4b4d5c",
              marginLeft: `${CHEVRON_GAP}px`,
              flexShrink: 0,
            }}
          />
        ) : null}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#9ca3af"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            flexShrink: 0,
            marginLeft: `${dividerBeforeChevron ? 8 : CHEVRON_GAP}px`,
            transition: "transform 0.2s ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <polyline points="6,9 12,15 18,9"></polyline>
        </svg>
      </div>

      {open && (
        <div
          style={{
            position: "absolute",
            top: `${height + 4}px`,
            left: 0,
            right: 0,
            backgroundColor: background,
            border: `1px solid ${borderColor}`,
            borderRadius: "6px",
            zIndex: 1000,
            maxHeight: "220px",
            overflowY: "auto",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
          }}
        >
          {normalized.length === 0 ? (
            <div style={{ padding: "10px 12px", color: "#9ca3af", fontSize: "13px" }}>
              {noOptionsText}
            </div>
          ) : (
            normalized.map((opt) => (
              <div
                key={opt.value}
                onClick={() => {
                  onChange(opt.value)
                  setOpen(false)
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  cursor: "pointer",
                  color: value === opt.value ? selectedOptionColor : "#fff",
                  fontSize: "14px",
                  backgroundColor: value === opt.value ? selectedOptionBackground : "transparent",
                }}
                onMouseEnter={(e) => {
                  if (value !== opt.value) e.currentTarget.style.backgroundColor = "#3A3B47"
                }}
                onMouseLeave={(e) => {
                  if (value !== opt.value) e.currentTarget.style.backgroundColor = "transparent"
                }}
              >
                {icon ? icon(opt.value) : null}
                <span style={{ textTransform: "capitalize" }}>{opt.label}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}
